import {
  Button,
  Grid,
  Pagination,
  PaginationProps,
  Select,
  Table,
  TableProps,
  Tag,
  message,
} from "antd";
import { CustomPaginationItem } from "components/Desktop/CustomPaginationItem";
import MobileOrderHistoryItem from "components/Mobile/MobileOrderHistoryItem";
import {
  E_ORDER_STATUS,
  E_PAYMENT_STATUS,
  OrderStatusLabels,
  OrderStatusTagColor,
  PaymentStatusLabels,
  PaymentStatusTagColor,
} from "constants/common";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetMyOrderList, useRepeatOrder } from "services/order";
import { IOrder, IOrderItems } from "types/order";
import { formatDateTime } from "utils/datetime";

const OrderHistoryForm = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterOrderStatus, setFilterOrderStatus] = useState<E_ORDER_STATUS>(
    E_ORDER_STATUS.ALL
  );
  const { orderList } = useGetMyOrderList(filterOrderStatus, currentPage);
  const navigate = useNavigate();
  const { repeatOrderAsync } = useRepeatOrder();
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingOrderId, setLoadingOrderId] = useState<number | null>(null);
  const onChangePage: PaginationProps["onChange"] = (page) => {
    setCurrentPage(page);
  };

  const handleOnChangeOrderStatus = (value: E_ORDER_STATUS) => {
    setFilterOrderStatus(value);
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/orderDetail/${orderId}`);
  };

  const handleRepeatOrder = async (e: React.MouseEvent, orderId: number) => {
    e.stopPropagation(); // Prevent navigation to order detail page

    try {
      setLoadingOrderId(orderId); // Set loading state for this specific order
      const result = await repeatOrderAsync(orderId);
      messageApi.success("เพิ่มสินค้าลงในตะกร้าเรียบร้อยแล้ว");
      const productIds = result.map((item) => item.productPrice?.product.id);
      navigate("/cart?products=" + productIds.join(","));
    } catch (error) {
      console.error("Failed to repeat order:", error);
      messageApi.error(
        "ไม่สามารถเพิ่มสินค้าลงในตะกร้าได้ กรุณาลองใหม่อีกครั้ง"
      );
    } finally {
      setLoadingOrderId(null); // Clear loading state when done
    }
  };

  const renderMobileView = () => {
    if (!orderList?.items?.length) {
      return <div className="text-center py-8">ไม่พบรายการสั่งซื้อ</div>;
    }

    return (
      <div className="space-y-4">
        {orderList.items.map((order) => (
          <MobileOrderHistoryItem
            key={order.id}
            order={order}
            onClick={() => handleOrderClick(order.id)}
          />
        ))}
      </div>
    );
  };
  const columns: TableProps<IOrder>["columns"] = [
    {
      title: "หมายเลขคำสั่งซื้อ",
      dataIndex: "id",
      key: "id",
      width: "15%",
    },
    {
      title: "รายการสินค้า",
      dataIndex: "orderItems",
      key: "orderItems",
      width: "10%",
      render: (orderItems: IOrderItems[]) => {
        return <div>{orderItems.length}</div>;
      },
    },
    {
      title: "ราคารวม",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: "15%",
      render: (text: number) => {
        return <div>฿{text.toLocaleString()}</div>;
      },
    },
    // {
    //   title: "คะแนนที่ใช้ไป",
    //   dataIndex: "orderItems",
    //   key: "pointsUsed",
    //   width: "15%",
    //   render: (orderItems: IOrderItems[], record: IOrder) => {
    //     const orderLevelPoints = record.pointsAsCashUsed || 0;
    //     const itemLevelPoints = orderItems.reduce(
    //       (total, item) => total + item.pointsUsed * item.quantity,
    //       0
    //     );
    //     const pointsToShow =
    //       orderLevelPoints > 0 ? orderLevelPoints : itemLevelPoints;
    //     return (
    //       <div>
    //         {pointsToShow > 0 ? `${pointsToShow.toLocaleString()} คะแนน` : "-"}
    //       </div>
    //     );
    //   },
    // },
    {
      title: "วัน เวลาที่มีการสั่งซื้อ",
      dataIndex: "orderDate",
      key: "orderDate",
      width: "20%",
      render: (text: string) => {
        return <div>{formatDateTime(text)}</div>;
      },
    },
    {
      title: "สถานะการสั่งซื้อ",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (text: E_ORDER_STATUS) => {
        return (
          <div>
            <Tag color={OrderStatusTagColor[text]}>
              {OrderStatusLabels[text]}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "สถานะการชำระเงิน",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: "15%",
      render: (text: E_PAYMENT_STATUS) => {
        return (
          <div>
            <Tag color={PaymentStatusTagColor[text]}>
              {PaymentStatusLabels[text]}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "id",
      key: "orderItemsId",
      width: "10%",
      render: (id: number, record: IOrder) => {
        if (record.status === E_ORDER_STATUS.COMPLETED) {
          return (
            <Button
              loading={loadingOrderId === id}
              onClick={(e) => handleRepeatOrder(e, id)}
              disabled={loadingOrderId !== null && loadingOrderId !== id}
            >
              ซื้ออีกครั้ง
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const renderDesktopView = () => {
    return (
      <Table
        onRow={(record) => {
          return {
            className: "cursor-pointer",
            onClick: () => {
              navigate(`/orderDetail/${record.id}`);
            },
          };
        }}
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={orderList?.items}
        pagination={false}
      />
    );
  };

  return (
    <div className="min-h-screen">
      {contextHolder}
      <div className="block md:flex justify-between mb-5 items-center">
        <div className="text-primary font-semibold">รายการสั่งซื้อ</div>
        <div className="flex gap-4 items-center justify-end md:justify-normal">
          <div className="hidden md:block">สถานะการสั่งซื้อ</div>
          <div>
            <Select
              className="order-history-select md:w-[175px]"
              onChange={handleOnChangeOrderStatus}
              size="large"
              value={filterOrderStatus}
              options={[
                {
                  label: OrderStatusLabels.ALL,
                  value: E_ORDER_STATUS.ALL,
                },
                {
                  label: OrderStatusLabels.COMPLETED,
                  value: E_ORDER_STATUS.COMPLETED,
                },
                {
                  label: OrderStatusLabels.CONFIRMED,
                  value: E_ORDER_STATUS.CONFIRMED,
                },
                {
                  label: OrderStatusLabels.PENDING,
                  value: E_ORDER_STATUS.PENDING,
                },
                {
                  label: OrderStatusLabels.CANCELLED,
                  value: E_ORDER_STATUS.CANCELLED,
                },
              ]}
            />
          </div>
        </div>
      </div>

      {screens.md ? renderDesktopView() : renderMobileView()}

      <div className="mt-4 justify-center flex">
        <Pagination
          itemRender={CustomPaginationItem}
          current={currentPage}
          onChange={onChangePage}
          total={orderList?.meta.totalItems}
          showSizeChanger={false}
          pageSize={20}
        />
      </div>
    </div>
  );
};
export default OrderHistoryForm;
